import React, { useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { usePeopleReader } from '../hooks/usePeopleReader'
import NavBar from '../components/layout/NavBar'
import BookingCalendar from '../components/booking/BookingCalendar'
import BookingDaySlots from '../components/booking/BookingDaySlots'
import NextAvailableButton from '../components/booking/NextAvailableButton'
import PeopleActionRail from '../components/PeopleActionRail'
import CommentsDrawer from '../components/comments/CommentsDrawer'
import GiftDrawer from '../components/gifts/GiftDrawer'
import GiftToast from '../components/gifts/GiftToast'
import GiftCelebration from '../components/gifts/GiftCelebration'
import { useGiftCatalog } from '../hooks/useGifts'
import { sendPeopleUserGift } from '../api/gifts'
import ReviewsInline from '../components/comments/ReviewsInline'
import ReviewSummary from '../components/comments/ReviewSummary'
import { usePersonPosts } from '../hooks/usePersonPosts'
import { PixeCard } from '../components/ui/PixeCard'
import { usePersonProducts } from '../hooks/usePersonProducts'
import { useListingsComments } from '../hooks/useListingsComments'
import ReportModal from '../components/moderation/ReportModal'
import { resolveLandscapeCover } from '../lib/cover'
import { resolveMediaUrl } from '../lib/mediaUrl'
import { paidBadgeLabel, type AccessLevel } from '../features/editor/access'
import { newIdempotencyKey } from '../lib/idempotency'
import { getToken } from '../lib/auth'
import { addDaysIso, getMonthDates, monthStartIso, todayIsoDate } from '../lib/bookingCalendar'
import { followProfile, unfollowProfile } from '../api/people'
import { computeFollowOptimistic, type FollowState } from '../lib/followState'
import SubscribeModal from '../components/subscriptions/SubscribeModal'
import { subscribeToCreator } from '../api/subscriptions'
import { getCreatorSubscriptionSettingsPublic, type CreatorSubscriptionSettingsPublic } from '../api/creatorSubscriptions'
import Lightbox, { type LightboxPhoto } from '../components/Lightbox'
import { unlockPixePhotoAlbum } from '../api/pixePhotos'
import { getCreatorEmailPreference, patchCreatorEmailPreference } from '../api/subscriptionEmail'
import ReputationBadge from '../components/Profile/ReputationBadge'
import ReputationModal from '../components/Profile/ReputationModal'
import { fetchProfileReputation, type ReputationPayload } from '../api/reputation'
import PixeProsSection from '../components/Profile/PixeProsSection'
import SectionHeader from '../components/Profile/SectionHeader'
import { bookingAvailability, cancelBookingWithReason, createBooking, listProPublicBookingServices, quoteBooking, rescheduleBooking } from '../api/bookings'
import type { BookingQuoteResponse, BookingService, BookingSlot, BookingStaffMember } from '../types/bookings'
import { apiCheckExistingDm, apiOpenDm } from '../features/messages/contactIntent'
import ContactIntentModal from '../components/messages/ContactIntentModal'
import { useMe } from '../hooks/useMe'
import MagazineHtml from '../components/renderers/MagazineHtml'

const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null

function BookingPaymentConfirm({
  onSuccess,
  onError,
  busy,
  setBusy,
}: {
  onSuccess: () => void
  onError: (message: string) => void
  busy: boolean
  setBusy: (value: boolean) => void
}) {
  const stripe = useStripe()
  const elements = useElements()

  const onConfirm = async () => {
    if (!stripe || !elements || busy) return
    setBusy(true)
    onError('')
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })
    if (result.error) {
      onError(result.error.message || 'Payment confirmation failed.')
      setBusy(false)
      return
    }
    setBusy(false)
    onSuccess()
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy || !stripe || !elements}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
      >
        {busy ? 'Processing...' : 'Pay deposit'}
      </button>
    </div>
  )
}

function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

function fromDateTimeLocalValue(value: string): string | null {
  const raw = String(value || '').trim()
  if (!raw) return null
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

export default function PeopleReader() {
  const navigate = useNavigate()
  const location = useLocation() as any
  const { publicId } = useParams()
  const { data, loading, error, reload } = usePeopleReader(publicId)
  const hero = data?.hero
  const aboutHtml = data?.about?.long?.html || data?.story?.body?.html
  const aboutPlain = data?.about?.shortBio || data?.story?.body?.plain
  const storyTitle = (data?.story as any)?.title?.trim?.() || ''
  const storySubtitle = (data?.story as any)?.subtitle?.trim?.() || ''
  const counts = (data as any)?.counts || {}
  const rawHandle = data?._meta?.handle || hero?.handle || ''
  const profileHandle = rawHandle.replace(/^@/, '').trim()
  const followersHref = profileHandle ? `/p/${encodeURIComponent(profileHandle)}/followers` : ''
  const followingHref = profileHandle ? `/p/${encodeURIComponent(profileHandle)}/following` : ''
  const giftsHref = profileHandle ? `/p/${encodeURIComponent(profileHandle)}/gifts` : ''
  const reactionsHref = profileHandle ? `/p/${encodeURIComponent(profileHandle)}/reactions` : ''
  const [followState, setFollowState] = useState<FollowState | null>(null)
  const [followPending, setFollowPending] = useState(false)
  const isSelf = Boolean(data?.actions?.isSelf)
  const canFollow = Boolean(data?.actions?.canFollow)
  const derivedFollowers = Number(data?.followers_count ?? data?.stats?.followers ?? 0)
  const derivedFollowing = Number(data?.following_count ?? data?.stats?.following ?? 0)
  const derivedIsFollowing = Boolean(data?.is_following ?? data?.actions?.isFollowing ?? false)
  const followersCount = followState?.followersCount ?? derivedFollowers
  const followingCount = derivedFollowing
  const giftsCount = Number((data as any)?.total_gifts_received_pixe ?? data?.stats?.gifts ?? 0)
  const [reputation, setReputation] = useState<ReputationPayload | null>(null)
  const [reputationOpen, setReputationOpen] = useState(false)
  const isFollowing = followState?.isFollowing ?? derivedIsFollowing
  const followDisabled = followPending || !canFollow
  const followLabel = followPending ? 'Working...' : (isFollowing ? 'Following' : 'Follow')
  const worksFeatured = useMemo(() => (data as any)?.works?.featured ?? [], [data])
  const worksRecent = useMemo(() => (data as any)?.works?.recent ?? [], [data])
  const pixePros = (data as any)?.pixe_pros
  const commentsLeft = useMemo(() => (data as any)?.comments?.written ?? [], [data])
  const profileComments = useMemo(() => (data as any)?.profileComments ?? [], [data])
  const creatorUserId = Number((hero as any)?.user_id ?? (data as any)?._meta?.user_id ?? 0) || null
  const { me } = useMe(Boolean(getToken()))
  const isViewingOwnProfile = Boolean(
    isSelf || (creatorUserId && me?.id && Number(creatorUserId) === Number(me.id))
  )
  const [creatorSubsPublic, setCreatorSubsPublic] = useState<CreatorSubscriptionSettingsPublic | null>(null)
  const [creatorSubsPublicLoading, setCreatorSubsPublicLoading] = useState(false)
  const [creatorSubsPublicError, setCreatorSubsPublicError] = useState<string | null>(null)
  const [bookingDate, setBookingDate] = useState('')
  const [bookingVisibleMonth, setBookingVisibleMonth] = useState('')
  const [bookingSlots, setBookingSlots] = useState<BookingSlot[]>([])
  const [bookingServices, setBookingServices] = useState<BookingService[]>([])
  const [bookingServiceId, setBookingServiceId] = useState<number | null>(null)
  const [bookingStaffMode, setBookingStaffMode] = useState<'ANY' | 'SPECIFIC'>('ANY')
  const [bookingStaffId, setBookingStaffId] = useState<number | null>(null)
  const [bookingStaffList, setBookingStaffList] = useState<BookingStaffMember[]>([])
  const [bookingDurationMinutes, setBookingDurationMinutes] = useState(30)
  const [bookingQuote, setBookingQuote] = useState<BookingQuoteResponse | null>(null)
  const [bookingLoading, setBookingLoading] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const [bookingReasonCode, setBookingReasonCode] = useState<'DISABLED' | 'CLOSED_SPECIAL' | 'NO_HOURS' | 'OUTSIDE_WINDOW' | 'LEAD_TIME' | null>(null)
  const [bookingEnabled, setBookingEnabled] = useState(true)
  const [bookingClosed, setBookingClosed] = useState(false)
  const [bookingSelected, setBookingSelected] = useState<BookingSlot | null>(null)
  const [bookingOpen, setBookingOpen] = useState(false)
  const [bookingSubmitting, setBookingSubmitting] = useState(false)
  const [bookingPaymentSubmitting, setBookingPaymentSubmitting] = useState(false)
  const [bookingClientSecret, setBookingClientSecret] = useState('')
  const [bookingPaymentExpiresAt, setBookingPaymentExpiresAt] = useState<string | null>(null)
  const [bookingPaymentRemainingSeconds, setBookingPaymentRemainingSeconds] = useState(0)
  const [bookingName, setBookingName] = useState('')
  const [bookingEmail, setBookingEmail] = useState('')
  const [bookingPhone, setBookingPhone] = useState('')
  const [bookingNotes, setBookingNotes] = useState('')
  const [latestBooking, setLatestBooking] = useState<{ public_id: string; starts_at: string; ends_at: string; payment_status: 'NONE' | 'REQUIRES_PAYMENT' | 'PAID' | 'FAILED' | 'REFUNDED' } | null>(null)
  const [bookingActionBusy, setBookingActionBusy] = useState(false)
  const [bookingRescheduleOpen, setBookingRescheduleOpen] = useState(false)
  const [bookingRescheduleDateTime, setBookingRescheduleDateTime] = useState('')
  const [bookingFindingNext, setBookingFindingNext] = useState(false)
  const bookingDayCacheRef = useRef<Record<string, {
    slots: BookingSlot[]
    enabled: boolean
    closed: boolean
    reasonCode: 'DISABLED' | 'CLOSED_SPECIAL' | 'NO_HOURS' | 'OUTSIDE_WINDOW' | 'LEAD_TIME' | null
    durationMinutes: number
    staffMode: 'ANY' | 'SPECIFIC'
    staffList: BookingStaffMember[]
  }>>({})
  const [bookingDaySummaries, setBookingDaySummaries] = useState<Record<string, {
    availableCount: number
    enabled: boolean
    closed: boolean
    loading?: boolean
  }>>({})

  React.useEffect(() => {
    if (!bookingPaymentExpiresAt) {
      setBookingPaymentRemainingSeconds(0)
      return
    }
    const tick = () => {
      const ms = new Date(bookingPaymentExpiresAt).getTime() - Date.now()
      setBookingPaymentRemainingSeconds(Math.max(0, Math.floor(ms / 1000)))
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [bookingPaymentExpiresAt])

  React.useEffect(() => {
    if (!bookingClientSecret || bookingPaymentRemainingSeconds > 0 || !profileHandle || !bookingDate) return
    setBookingError('Payment session expired — please pick a time again.')
    setBookingClientSecret('')
    setBookingPaymentExpiresAt(null)
    setLatestBooking(null)
    setBookingOpen(false)
    setBookingSelected(null)
    bookingAvailability({
      host_type: 'pro',
      handle: profileHandle,
      date: bookingDate,
      service_id: bookingServiceId ?? undefined,
      staff_mode: bookingStaffMode,
      staff_id: bookingStaffMode === 'SPECIFIC' ? (bookingStaffId ?? undefined) : undefined,
    })
      .then((row) => {
        const slots = Array.isArray(row.slots) ? row.slots : []
        const staffList = Array.isArray(row.staff_list) ? row.staff_list : []
        setBookingSlots(slots)
        setBookingEnabled(Boolean(row.enabled))
        setBookingClosed(Boolean(row.closed))
        setBookingReasonCode(row.reason_code || null)
        setBookingDurationMinutes(Number(row.duration_minutes || 30))
        setBookingStaffList(staffList)
        setBookingStaffMode(row.staff_mode === 'SPECIFIC' ? 'SPECIFIC' : 'ANY')
        bookingDayCacheRef.current = {
          ...bookingDayCacheRef.current,
          [bookingDate]: {
            slots,
            enabled: Boolean(row.enabled),
            closed: Boolean(row.closed),
            reasonCode: row.reason_code || null,
            durationMinutes: Number(row.duration_minutes || 30),
            staffMode: row.staff_mode === 'SPECIFIC' ? 'SPECIFIC' : 'ANY',
            staffList,
          },
        }
        setBookingDaySummaries((prev) => ({
          ...prev,
          [bookingDate]: {
            availableCount: slots.length,
            enabled: Boolean(row.enabled),
            closed: Boolean(row.closed),
          },
        }))
      })
      .catch(() => {})
  }, [bookingClientSecret, bookingPaymentRemainingSeconds, profileHandle, bookingDate, bookingServiceId, bookingStaffMode, bookingStaffId])

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [publicId])

  const applyBookingDay = useCallback((date: string, payload: {
    slots: BookingSlot[]
    enabled: boolean
    closed: boolean
    reasonCode: 'DISABLED' | 'CLOSED_SPECIAL' | 'NO_HOURS' | 'OUTSIDE_WINDOW' | 'LEAD_TIME' | null
    durationMinutes: number
    staffMode: 'ANY' | 'SPECIFIC'
    staffList: BookingStaffMember[]
  }) => {
    bookingDayCacheRef.current = { ...bookingDayCacheRef.current, [date]: payload }
    setBookingDaySummaries((prev) => ({
      ...prev,
      [date]: {
        availableCount: payload.slots.length,
        enabled: payload.enabled,
        closed: payload.closed,
      },
    }))
  }, [])

  const fetchBookingDay = useCallback(
    async (date: string, options: { force?: boolean; quiet?: boolean } = {}) => {
      if (!profileHandle || !date || !pixePros?.is_enabled) return null
      const cached = bookingDayCacheRef.current[date]
      if (cached && !options.force) return cached
      if (!options.quiet) {
        setBookingLoading(true)
        setBookingError(null)
      } else {
        setBookingDaySummaries((prev) => ({ ...prev, [date]: { ...(prev[date] || { availableCount: 0, enabled: true, closed: false }), loading: true } }))
      }
      try {
        const row = await bookingAvailability({
          host_type: 'pro',
          handle: profileHandle,
          date,
          service_id: bookingServiceId ?? undefined,
          staff_mode: bookingStaffMode,
          staff_id: bookingStaffMode === 'SPECIFIC' ? (bookingStaffId ?? undefined) : undefined,
        })
        const payload = {
          slots: Array.isArray(row.slots) ? row.slots : [],
          enabled: Boolean(row.enabled),
          closed: Boolean(row.closed),
          reasonCode: row.reason_code || null,
          durationMinutes: Number(row.duration_minutes || 30),
          staffMode: row.staff_mode === 'SPECIFIC' ? 'SPECIFIC' : 'ANY',
          staffList: Array.isArray(row.staff_list) ? row.staff_list : [],
        } as const
        applyBookingDay(date, payload)
        return payload
      } catch (e: any) {
        if (!options.quiet) {
          setBookingSlots([])
          setBookingEnabled(true)
          setBookingClosed(false)
          setBookingReasonCode(null)
          setBookingError(e?.response?.data?.detail || e?.message || 'Failed to load availability')
        }
        return null
      } finally {
        if (!options.quiet) {
          setBookingLoading(false)
        }
        setBookingDaySummaries((prev) => {
          if (!prev[date]) return prev
          return { ...prev, [date]: { ...prev[date], loading: false } }
        })
      }
    },
    [applyBookingDay, bookingServiceId, bookingStaffMode, bookingStaffId, pixePros?.is_enabled, profileHandle],
  )

  const loadSelectedBookingDay = useCallback(async (date: string, options: { force?: boolean } = {}) => {
    const payload = await fetchBookingDay(date, { force: options.force })
    if (!payload) return
    setBookingSlots(payload.slots)
    setBookingEnabled(payload.enabled)
    setBookingClosed(payload.closed)
    setBookingReasonCode(payload.reasonCode)
    setBookingDurationMinutes(payload.durationMinutes)
    setBookingStaffList(payload.staffList)
    setBookingStaffMode(payload.staffMode)
  }, [fetchBookingDay])

  React.useEffect(() => {
    if (bookingDate) return
    const today = todayIsoDate()
    setBookingDate(today)
    setBookingVisibleMonth(monthStartIso(today))
  }, [bookingDate])

  React.useEffect(() => {
    if (!pixePros?.is_enabled || !profileHandle) return
    let cancelled = false
    listProPublicBookingServices(profileHandle)
      .then((rows) => {
        if (cancelled) return
        setBookingServices(rows)
        if (!bookingServiceId && rows.length > 0) setBookingServiceId(rows[0].id)
      })
      .catch(() => {
        if (!cancelled) setBookingServices([])
      })
    return () => {
      cancelled = true
    }
  }, [pixePros?.is_enabled, profileHandle])

  React.useEffect(() => {
    if (!pixePros?.is_enabled || !profileHandle || !bookingDate) return
    void loadSelectedBookingDay(bookingDate)
  }, [pixePros?.is_enabled, profileHandle, bookingDate, bookingServiceId, bookingStaffMode, bookingStaffId, loadSelectedBookingDay])

  React.useEffect(() => {
    if (!bookingDate) return
    setBookingVisibleMonth(monthStartIso(bookingDate))
  }, [bookingDate])

  React.useEffect(() => {
    bookingDayCacheRef.current = {}
    setBookingDaySummaries({})
  }, [bookingServiceId, bookingStaffMode, bookingStaffId])

  React.useEffect(() => {
    if (!pixePros?.is_enabled || !profileHandle || !bookingVisibleMonth) return
    let cancelled = false
    const monthDates = getMonthDates(bookingVisibleMonth)
    const queue = monthDates.filter((date) => !bookingDayCacheRef.current[date])
    if (!queue.length) return
    let cursor = 0
    const worker = async () => {
      while (!cancelled) {
        const idx = cursor
        cursor += 1
        if (idx >= queue.length) return
        await fetchBookingDay(queue[idx], { quiet: true })
      }
    }
    void Promise.all([worker(), worker(), worker()])
    return () => {
      cancelled = true
    }
  }, [bookingVisibleMonth, bookingServiceId, fetchBookingDay, pixePros?.is_enabled, profileHandle])

  React.useEffect(() => {
    if (!pixePros?.is_enabled || !profileHandle) return
    let cancelled = false
    quoteBooking({ host_type: 'pro', host_ref: profileHandle, service_id: bookingServiceId ?? undefined })
      .then((q) => {
        if (!cancelled) setBookingQuote(q)
      })
      .catch(() => {
        if (!cancelled) setBookingQuote(null)
      })
    return () => {
      cancelled = true
    }
  }, [pixePros?.is_enabled, profileHandle, bookingServiceId])

  const subsEnabled = Boolean(creatorSubsPublic?.is_enabled) && Boolean(creatorUserId)
  const [subOpen, setSubOpen] = useState(false)
  const [subBusy, setSubBusy] = useState(false)
  const [subError, setSubError] = useState<string | null>(null)
  const [subscribed, setSubscribed] = useState(false)
  const [receivePostsEmail, setReceivePostsEmail] = useState(false)

  // Photos lightbox (People profile)
  const [photoLightboxOpen, setPhotoLightboxOpen] = useState(false)
  const [photoLightboxIndex, setPhotoLightboxIndex] = useState(0)
  const unlockedPhotoItems = useMemo(() => {
    return Array.isArray((data as any)?.photos) ? ((data as any).photos as any[]) : []
  }, [data])
  const lightboxPhotos = useMemo(() => {
    return unlockedPhotoItems.map((p) => ({
      id: String(p?.id ?? ''),
      image_url: String(p?.image_url || ''),
      caption: (p?.caption || '').toString(),
      is_featured: Boolean(p?.is_featured),
    })) as LightboxPhoto[]
  }, [unlockedPhotoItems])

  // Optional deep-linking: #photo=<id>
  React.useEffect(() => {
    const h = (location?.hash || '').toString()
    if (!h.startsWith('#photo=')) return
    const id = decodeURIComponent(h.slice('#photo='.length))
    const idx = lightboxPhotos.findIndex((p) => p.id === id)
    if (idx >= 0) {
      setPhotoLightboxIndex(idx)
      setPhotoLightboxOpen(true)
    }
  }, [location?.hash, lightboxPhotos])

  React.useEffect(() => {
    if (!photoLightboxOpen) return
    const id = lightboxPhotos[photoLightboxIndex]?.id
    if (!id) return
    try {
      history.replaceState(null, '', `#photo=${encodeURIComponent(id)}`)
    } catch {}
  }, [photoLightboxOpen, photoLightboxIndex, lightboxPhotos])

  // Posts authored by this person (web-specific fetch)
  const { items: posts, canLoadMore: postsCanMore, loadMore: loadMorePosts, loading: postsLoading } = usePersonPosts(publicId)
  // No extra profile sections unless subscriptions are enabled.
  const showSubCta = subsEnabled

  // Responsive cols → show exactly 2 rows per viewport width
  const getCols = () => {
    const w = window.innerWidth
    if (w >= 1024) return 4 // lg
    if (w >= 768) return 3 // md
    if (w >= 640) return 2 // sm
    return 1
  }
  const [cols, setCols] = useState<number>(typeof window !== 'undefined' ? getCols() : 4)
  const [postLimit, setPostLimit] = useState<number>(cols * 2)
  const step = cols * 2
  const visiblePosts = useMemo(() => posts.slice(0, postLimit), [posts, postLimit])
  const canSeeMore = posts.length > postLimit || postsCanMore
  const onSeeMore = async () => {
    if (posts.length <= postLimit && postsCanMore) await loadMorePosts()
    setPostLimit((n) => n + step)
  }
  // Update limits on resize
  React.useEffect(() => {
    const onResize = () => {
      const c = getCols()
      setCols(c)
      setPostLimit((prev) => {
        const base = c * 2
        return Math.max(base, prev)
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Products (listings) — show two rows
  const { items: products, canLoadMore: productsCanMore, loadMore: loadMoreProducts, loading: productsLoading } = usePersonProducts(publicId)
  const [productLimit, setProductLimit] = useState<number>(cols * 2)
  const productStep = cols * 2
  const visibleProducts = useMemo(() => products.slice(0, productLimit), [products, productLimit])
  const canSeeMoreProducts = products.length > productLimit || productsCanMore
  const onSeeMoreProducts = async () => {
    if (products.length <= productLimit && productsCanMore) await loadMoreProducts()
    setProductLimit((n) => n + productStep)
  }
  React.useEffect(() => {
    const update = () => {
      const c = getCols()
      setProductLimit((prev) => Math.max(c * 2, prev))
    }
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Comments on products/services (aggregate comments from listings)
  const listingRefs = useMemo(
    () =>
      products.map((l) => ({
        id: l.public_id,
        title: l.product?.title || 'Product',
        image: l.product?.thumbnail_media?.public_url || l.images?.[0]?.public_url || null,
      })),
    [products]
  )
  const {
    items: productComments,
    loading: productCommentsLoading,
    canLoadMore: productCommentsCanMore,
    loadMore: loadMoreProductComments,
  } = useListingsComments(listingRefs, { perListing: 2, batch: 4 })

  // Comments drawer state
  const [commentsOpen, setCommentsOpen] = useState(false)
  const openComments = () => setCommentsOpen(true)
  const closeComments = () => setCommentsOpen(false)
  const [reportOpen, setReportOpen] = useState(false)
  const openReport = () => setReportOpen(true)
  const closeReport = () => setReportOpen(false)

  const [contactIntentOpen, setContactIntentOpen] = useState(false)
  const [contactIntentBusy, setContactIntentBusy] = useState(false)
  const [contactIntentTargetId, setContactIntentTargetId] = useState<string>('')
  const [contactIntentTargetName, setContactIntentTargetName] = useState<string>('')
  const [contactIntentTargetHandle, setContactIntentTargetHandle] = useState<string>('')
  const [contactIntentContext, setContactIntentContext] = useState<{ postId?: string } | undefined>(undefined)

  const trackContactEvent = useCallback((event: string, props: Record<string, unknown>) => {
    const anyWindow = window as any
    try {
      if (anyWindow?.analytics?.track && typeof anyWindow.analytics.track === 'function') {
        anyWindow.analytics.track(event, props)
      }
      window.dispatchEvent(
        new CustomEvent('pixeloop:analytics', {
          detail: { event, props },
        })
      )
    } catch {}
  }, [])

  const openContactIntentModal = useCallback(
    async (targetUserId: number, targetUserName: string, context?: { postId?: string }, targetHandle?: string) => {
    if (!targetUserId || isViewingOwnProfile) return
    if (!getToken()) {
      navigate('/login')
      return
    }
    trackContactEvent('post_message_clicked', {
      surface: 'people_reader',
      target_user_id: targetUserId,
      profile_public_id: publicId,
      context_post_id: context?.postId || null,
    })
    setContactIntentBusy(true)
    try {
      const existing = await apiCheckExistingDm(targetUserId)
      if (existing.threadId) {
        trackContactEvent('contact_intent_selected', {
          type: 'chat',
          targetUserId,
          contextPostId: context?.postId || null,
          skipped_modal: true,
        })
        navigate(`/inbox/i/${existing.threadId}`)
        return
      }
      setContactIntentTargetId(String(targetUserId))
      setContactIntentTargetName(targetUserName || 'this user')
      setContactIntentTargetHandle((targetHandle || '').replace(/^@/, '').trim())
      setContactIntentContext(context)
      setContactIntentOpen(true)
    } finally {
      setContactIntentBusy(false)
    }
  }, [isViewingOwnProfile, navigate, publicId, trackContactEvent])

  const onMessageFromRail = useCallback(async () => {
    if (!creatorUserId) return
    await openContactIntentModal(
      creatorUserId,
      hero?.displayName || 'this user',
      { postId: publicId || undefined },
      hero?.handle || undefined
    )
  }, [creatorUserId, hero?.displayName, hero?.handle, openContactIntentModal, publicId])

  const onContactIntentSelect = useCallback(async (intent: 'chat' | 'message') => {
    if (!contactIntentTargetId) return
    const targetUserId = Number(contactIntentTargetId)
    if (!Number.isFinite(targetUserId) || targetUserId <= 0) return
    setContactIntentBusy(true)
    try {
      trackContactEvent('contact_intent_selected', {
        type: intent,
        targetUserId,
        contextPostId: contactIntentContext?.postId || null,
      })
      if (intent === 'chat') {
        const { threadId } = await apiOpenDm(targetUserId)
        setContactIntentOpen(false)
        navigate(`/inbox/i/${threadId}`)
        return
      }
      setContactIntentOpen(false)
      const qs = new URLSearchParams()
      if (contactIntentTargetHandle) qs.set('recipient', `@${contactIntentTargetHandle}`)
      qs.set('recipient_id', String(targetUserId))
      if (contactIntentContext?.postId) qs.set('context_post', contactIntentContext.postId)
      qs.set('type', 'MESSAGE')
      navigate(`/inbox/compose?${qs.toString()}`)
    } finally {
      setContactIntentBusy(false)
    }
  }, [contactIntentContext?.postId, contactIntentTargetHandle, contactIntentTargetId, navigate, trackContactEvent])

  // Gifts drawer state
  const [giftOpen, setGiftOpen] = useState(false)
  const { items: catalog } = useGiftCatalog()
  const [toast, setToast] = useState<{ visible: boolean; kind?: 'success' | 'error'; emoji: string; label: string; detail?: string; celebrate?: boolean }>({ visible: false, kind: 'success', emoji: '🎁', label: '', celebrate: false })
  const hideToast = () => setToast((t) => ({ ...t, visible: false }))

  // Mobile-only: tap anywhere (non-interactive) to go back, matching Reader behavior.
  const tapRef = useRef<{ x: number; y: number; moved: boolean; t: number } | null>(null)
  const isMobile = () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false)
  const tapBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/people')
  }, [navigate])

  React.useEffect(() => {
    // Open drawers when coming from mobile rail actions.
    const st = location?.state || {}
    if (st.openComments) setCommentsOpen(true)
    if (st.openGift) setGiftOpen(true)
    // Clear state to avoid reopening on refresh/back-forward.
    try { window.history.replaceState({}, '', window.location.href) } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!data) return
    // Initialize follow state from server once. Do not overwrite optimistic updates
    // when followPending flips, otherwise the UI "reverts" even on success.
    setFollowState((prev) => prev ?? { isFollowing: derivedIsFollowing, followersCount: derivedFollowers })
  }, [data, derivedIsFollowing, derivedFollowers])

  React.useEffect(() => {
    if (!profileHandle) {
      setReputation(null)
      return
    }
    let cancelled = false
    fetchProfileReputation(profileHandle)
      .then((payload) => {
        if (!cancelled) setReputation(payload)
      })
      .catch(() => {
        if (!cancelled) setReputation(null)
      })
    return () => {
      cancelled = true
    }
  }, [profileHandle])

  // Fetch public creator subscription info (tiers + pricing) for this profile.
  React.useEffect(() => {
    if (!creatorUserId) return
    let cancelled = false
    setCreatorSubsPublicLoading(true)
    setCreatorSubsPublicError(null)
    getCreatorSubscriptionSettingsPublic(creatorUserId)
      .then((res) => {
        if (cancelled) return
        setCreatorSubsPublic(res)
        if (typeof res?.viewer_is_subscribed === 'boolean') {
          setSubscribed(Boolean(res.viewer_is_subscribed))
        }
      })
      .catch((e: any) => {
        if (cancelled) return
        setCreatorSubsPublic(null)
        setCreatorSubsPublicError(e?.response?.data?.detail || e?.message || 'Failed to load subscription info')
      })
      .finally(() => { if (!cancelled) setCreatorSubsPublicLoading(false) })
    return () => { cancelled = true }
  }, [creatorUserId])

  React.useEffect(() => {
    const authed = Boolean(getToken())
    if (!authed || !creatorUserId || isSelf) return
    getCreatorEmailPreference(creatorUserId)
      .then((pref) => setReceivePostsEmail(Boolean(pref.receive_posts)))
      .catch(() => {})
  }, [creatorUserId, isSelf])

  async function handleSendGift(g: any) {
    if (!creatorUserId) return
    try {
      const res: any = await sendPeopleUserGift(creatorUserId, { gift_id: g.gift.id, idempotency_key: newIdempotencyKey('giftp') })
      const receipt = res?.ledger_entry_id ? `Receipt #${res.ledger_entry_id}` : undefined
      const to = hero?.displayName ? `to ${hero.displayName}` : undefined
      setToast({ visible: true, kind: 'success', celebrate: true, emoji: g.gift.emoji, label: `Sent ${g.gift.name}`, detail: receipt ? (to ? `${to} - ${receipt}` : receipt) : to })
    } catch (e: any) {
      setToast({ visible: true, kind: 'error', celebrate: false, emoji: '⚠️', label: 'Gift failed', detail: e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Please try again' })
    } finally {
      setGiftOpen(false)
    }
  }

  async function handleFollowToggle() {
    if (!publicId || isSelf || !canFollow) return
    if (!getToken()) {
      navigate('/login')
      return
    }
    const previous = followState ?? { isFollowing: derivedIsFollowing, followersCount: derivedFollowers }
    const optimistic = computeFollowOptimistic(previous)
    setFollowState(optimistic)
    setFollowPending(true)
    try {
      const res = previous.isFollowing ? await unfollowProfile(publicId) : await followProfile(publicId)
      setFollowState({ isFollowing: res.is_following, followersCount: res.followers_count })
    } catch (e: any) {
      setFollowState(previous)
      if (e?.response?.status === 401) {
        navigate('/login')
        return
      }
      setToast({
        visible: true,
        kind: 'error',
        celebrate: false,
        emoji: '⚠️',
        label: 'Follow failed',
        detail: e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Please try again',
      })
    } finally {
      setFollowPending(false)
    }
  }

  async function handleCreateProBooking() {
    if (!bookingSelected || !profileHandle || bookingSubmitting) return
    const name = bookingName.trim()
    const email = bookingEmail.trim()
    if (!name || !email) {
      setBookingError('Name and email are required.')
      return
    }
    setBookingSubmitting(true)
    setBookingError(null)
    try {
      const created = await createBooking({
        host_type: 'pro',
        host_ref: profileHandle,
        service_id: bookingServiceId,
        staff_mode: bookingStaffMode,
        staff_id: bookingStaffMode === 'SPECIFIC' ? bookingStaffId : null,
        starts_at: bookingSelected.starts_at,
        customer_name: name,
        customer_email: email,
        customer_phone: bookingPhone.trim(),
        notes: bookingNotes.trim(),
      })
      if (created.client_secret) {
        setLatestBooking({
          public_id: created.booking_public_id || created.public_id,
          starts_at: created.starts_at,
          ends_at: created.ends_at,
          payment_status: created.payment_status,
        })
        setBookingClientSecret(created.client_secret)
        setBookingPaymentExpiresAt(created.payment_expires_at || null)
        setToast({
          visible: true,
          kind: 'success',
          celebrate: false,
          emoji: 'OK',
          label: 'Complete payment within MM:SS',
          detail: 'We emailed you a payment link to manage this booking.',
        })
        return
      }
      setLatestBooking({
        public_id: created.booking_public_id || created.public_id,
        starts_at: created.starts_at,
        ends_at: created.ends_at,
        payment_status: created.payment_status,
      })
      setBookingOpen(false)
      setBookingSelected(null)
      setBookingClientSecret('')
      setBookingPaymentExpiresAt(null)
      setBookingNotes('')
      const confirmationDetail = created.customer_email ? `Confirmed. We emailed a manage link to ${created.customer_email}.` : undefined
      setToast({ visible: true, kind: 'success', celebrate: false, emoji: 'OK', label: 'Booking confirmed', detail: confirmationDetail })
      await loadSelectedBookingDay(bookingDate, { force: true })
    } catch (e: any) {
      const code = e?.response?.data?.code
      if (code === 'SLOT_TAKEN') setBookingError('That slot was just taken. Please choose another.')
      else if (code === 'CAPACITY_FULL') setBookingError('That slot is at capacity. Please choose another.')
      else if (code === 'STAFF_UNAVAILABLE') setBookingError('Selected staff is unavailable for that slot.')
      else if (code === 'RATE_LIMITED') setBookingError('Too many booking attempts. Please try again later.')
      else setBookingError(e?.response?.data?.detail || e?.message || 'Failed to create booking')
    } finally {
      setBookingSubmitting(false)
    }
  }

  async function onBookingPaymentSuccess() {
    setBookingOpen(false)
    setBookingSelected(null)
    setBookingClientSecret('')
    setBookingPaymentExpiresAt(null)
    setBookingNotes('')
    setLatestBooking((prev) => prev ? ({ ...prev, payment_status: 'PAID' }) : prev)
    setToast({ visible: true, kind: 'success', celebrate: false, emoji: 'OK', label: 'Booking deposit paid' })
    if (!pixePros?.is_enabled || !profileHandle || !bookingDate) return
    try {
      await loadSelectedBookingDay(bookingDate, { force: true })
    } catch {}
  }

  async function onCancelLatestBooking() {
    if (!latestBooking || bookingActionBusy) return
    if (!window.confirm('Cancel this booking?')) return
    const reason = window.prompt('Cancel reason (optional):', '') || ''
    setBookingActionBusy(true)
    setBookingError(null)
    try {
      await cancelBookingWithReason(latestBooking.public_id, reason.trim())
      setLatestBooking(null)
      if (!pixePros?.is_enabled || !profileHandle || !bookingDate) return
      await loadSelectedBookingDay(bookingDate, { force: true })
      setToast({ visible: true, kind: 'success', celebrate: false, emoji: 'OK', label: 'Booking cancelled' })
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to cancel booking')
    } finally {
      setBookingActionBusy(false)
    }
  }

  async function onSubmitRescheduleLatestBooking() {
    if (!latestBooking || bookingActionBusy) return
    const nextIso = fromDateTimeLocalValue(bookingRescheduleDateTime)
    if (!nextIso) {
      setBookingError('Select a valid date and time to reschedule.')
      return
    }
    setBookingActionBusy(true)
    setBookingError(null)
    try {
      const updated = await rescheduleBooking(latestBooking.public_id, nextIso)
      setLatestBooking({
        public_id: updated.public_id,
        starts_at: updated.starts_at,
        ends_at: updated.ends_at,
        payment_status: updated.payment_status,
      })
      setBookingRescheduleOpen(false)
      setBookingRescheduleDateTime('')
      if (!pixePros?.is_enabled || !profileHandle || !bookingDate) return
      await loadSelectedBookingDay(bookingDate, { force: true })
      setToast({ visible: true, kind: 'success', celebrate: false, emoji: 'OK', label: 'Booking rescheduled' })
    } catch (e: any) {
      const code = e?.response?.data?.code
      if (code === 'SLOT_TAKEN') setBookingError('That slot is unavailable. Choose another time.')
      else if (code === 'CAPACITY_FULL') setBookingError('That slot is at capacity. Choose another time.')
      else if (code === 'STAFF_UNAVAILABLE') setBookingError('Assigned staff is unavailable. Choose another time.')
      else if (code === 'EXPIRED') setBookingError('This unpaid booking has expired. Please book again.')
      else setBookingError(e?.response?.data?.detail || e?.message || 'Failed to reschedule booking')
    } finally {
      setBookingActionBusy(false)
    }
  }

  async function onFindNextAvailableBookingDay() {
    if (!profileHandle || !bookingDate || bookingFindingNext) return
    setBookingFindingNext(true)
    setBookingError(null)
    try {
      for (let offset = 1; offset <= 30; offset += 1) {
        const date = addDaysIso(bookingDate, offset)
        const payload = await fetchBookingDay(date, { quiet: true })
        if (payload && payload.enabled && payload.slots.length > 0) {
          setBookingDate(date)
          setBookingVisibleMonth(monthStartIso(date))
          return
        }
      }
      setBookingError('No upcoming availability in the next 30 days.')
    } finally {
      setBookingFindingNext(false)
    }
  }

  const bookingEmptyMessage = useMemo(() => {
    if (bookingReasonCode === 'DISABLED' || !bookingEnabled) return 'Booking is currently disabled for this provider.'
    if (bookingReasonCode === 'CLOSED_SPECIAL') return 'Closed for a special/holiday schedule on this date.'
    if (bookingReasonCode === 'NO_HOURS' || bookingClosed) return 'No operating hours configured for this date.'
    if (bookingReasonCode === 'OUTSIDE_WINDOW') return 'This date is outside the current booking window.'
    if (bookingReasonCode === 'LEAD_TIME') return 'No slots meet the minimum lead time yet.'
    return 'No slots available for this date.'
  }, [bookingReasonCode, bookingEnabled, bookingClosed])

  // ---- Sticky sub‑nav items and active section tracking ----
	  const navItems = useMemo(() => {
	    const items: { id: string; label: string; count?: number }[] = []
	    const hasAbout = Boolean(aboutHtml || aboutPlain)
	    const hasLinks = Boolean(data?.links && data.links.length > 0)
	    const albumRows = Array.isArray((data as any)?.photo_albums) ? ((data as any).photo_albums as any[]) : []
	    const hasPhotos = albumRows.length > 0 || unlockedPhotoItems.length > 0
	    const photosCount = albumRows.reduce((sum, a) => sum + Number(a?.photo_count ?? 0), 0) || unlockedPhotoItems.length
	    if (hasAbout) items.push({ id: 'about', label: 'About' })
	    if (hasPhotos) items.push({ id: 'photos', label: 'Photos', count: photosCount })
	    items.push({ id: 'posts', label: 'Posts', count: posts.length })
	    items.push({ id: 'products', label: 'Products', count: products.length })
	    items.push({ id: 'reviews', label: 'Reviews' })
	    items.push({ id: 'comments', label: 'Comments' })
	    if (pixePros?.is_enabled) items.push({ id: 'booking', label: 'Booking' })
	    if (hasLinks) items.push({ id: 'links', label: 'Links' })
	    return items
	  }, [aboutHtml, aboutPlain, data?.links, (data as any)?.photo_albums, unlockedPhotoItems.length, posts.length, products.length, pixePros?.is_enabled])

  const [activeId, setActiveId] = useState<string | null>(null)
  React.useEffect(() => {
    const handler = () => {
      let current: string | null = navItems[0]?.id ?? null
      const offset = 100
      for (const it of navItems) {
        const el = document.getElementById(it.id)
        if (!el) continue
        const top = el.getBoundingClientRect().top
        if (top - offset <= 0) current = it.id
      }
      setActiveId(current)
    }
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('scroll', handler)
      window.removeEventListener('resize', handler)
    }
  }, [navItems])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div
      className="min-h-screen"
      onPointerDown={(e) => {
        if (!isMobile()) return
        const t = e.target as HTMLElement
        if (t?.closest?.('[data-no-tapback=\"1\"]')) return
        if (t?.closest?.('a,button,input,textarea,select,label,[role="button"],.copy-code-btn')) return
        tapRef.current = { x: e.clientX, y: e.clientY, moved: false, t: Date.now() }
      }}
      onPointerMove={(e) => {
        const s = tapRef.current
        if (!s) return
        const dx = Math.abs(e.clientX - s.x)
        const dy = Math.abs(e.clientY - s.y)
        if (dx > 12 || dy > 12) s.moved = true
      }}
      onPointerUp={(e) => {
        if (!isMobile()) return
        const s = tapRef.current
        tapRef.current = null
        if (!s) return
        const dt = Date.now() - s.t
        if (s.moved || dt > 650) return
        const t = e.target as HTMLElement
        if (t?.closest?.('[data-no-tapback=\"1\"]')) return
        if (t?.closest?.('a,button,input,textarea,select,label,[role="button"],.copy-code-btn')) return
        const sel = window.getSelection?.()
        if (sel && sel.toString().trim()) return
        tapBack()
      }}
    >
      <NavBar showSearch={false} showMobileModePill={false} />

      <main className="mx-auto max-w-6xl px-4 py-6 scroll-smooth">
        <div className="mb-3">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1)
              else navigate('/people')
            }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/85 hover:bg-white/10"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
              <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            Back to People
          </button>
        </div>

        {loading && <div className="rounded-xl bg-white/5 h-48" />}
        {error && <div className="text-sm text-red-400">{error}</div>}
        {hero && (
          <section className="mb-2 relative rounded-xl overflow-hidden">
            <div className="relative aspect-[16/9]">
              {hero.coverImage ? (
                <img src={resolveMediaUrl(hero.coverImage) || hero.coverImage} alt={hero.displayName} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-white/5" />
              )}
              {/* Stronger gradient + subtle radial */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_100%,rgba(0,0,0,0.6),transparent_70%)]" />
              </div>
              <div className="absolute left-4 bottom-4 flex items-center gap-4">
                <div className="relative">
                  {hero.avatar ? (
                    <img src={resolveMediaUrl(hero.avatar) || hero.avatar} className="h-20 w-20 rounded-full object-cover ring-2 ring-white/20 shadow-xl" />
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-white/10 ring-2 ring-white/10" />
                  )}
                  {hero.verified && (
                    <span className="absolute -bottom-1 -right-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white text-xs font-bold ring-2 ring-black/60">✓</span>
                  )}
                </div>
                <div className="backdrop-blur bg-black/30 rounded-xl px-3 py-2">
                  <div className="text-xl font-extrabold text-white">{hero.displayName}</div>
                  <div className="text-white/85 text-sm">{hero.handle}</div>
                  {hero.motto ? (
                    <div className="text-white/80 text-xs mt-0.5 italic leading-snug">{hero.motto}</div>
                  ) : null}
                </div>
              </div>
              {!isSelf && canFollow && (
                <div className="absolute right-4 bottom-4">
                  <div className="flex flex-col items-end gap-2 md:flex-row md:flex-wrap md:items-center md:justify-end">
                    {subsEnabled ? (
                      <div className="flex flex-col items-end gap-2 justify-end sm:flex-row sm:items-center">
                        {subscribed ? (
                          <>
                            <span className="rounded-full px-3 py-2 text-sm font-semibold shadow-lg border border-emerald-400/30 bg-emerald-500/10 text-emerald-50">
                              Subscribed
                            </span>
                            <Link
                              to="/settings/subscriptions"
                              className="rounded-full px-4 py-2 text-sm font-semibold shadow-lg border border-white/15 bg-white/5 text-white/90 hover:bg-white/10"
                            >
                              Manage
                            </Link>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (!getToken()) { navigate('/login'); return }
                              setSubError(null)
                              setSubOpen(true)
                            }}
                            className="rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition bg-brand text-black hover:brightness-110"
                          >
                            Subscribe
                          </button>
                        )}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleFollowToggle}
                      disabled={followDisabled}
                      className={`rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition ${isFollowing ? 'bg-white/10 text-white border border-white/20' : 'bg-brand text-black'} ${followDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:brightness-110'}`}
                    >
                      {followLabel}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Sticky sub‑nav */}
        {hero && (
          <ReputationBadge
            tier={reputation?.tier?.key || (data as any)?.reputation_tier || (hero as any)?.reputation_tier}
            score={reputation?.score ?? (data as any)?.reputation_score ?? (hero as any)?.reputation_score ?? null}
            onViewDetails={() => setReputationOpen(true)}
          />
        )}

        {navItems.length > 0 && (
          <div className="sticky top-0 z-30 -mx-4 mb-4 border-b border-white/10 bg-black/30 backdrop-blur">
            <nav className="mx-auto max-w-6xl px-4 flex items-center gap-4 overflow-x-auto py-2 hscroll no-scrollbar whitespace-nowrap">
              {navItems.map((it) => (
                <button
                  key={it.id}
                  onClick={() => scrollTo(it.id)}
                  className={`shrink-0 pb-1 text-sm ${activeId === it.id ? 'text-white font-extrabold border-b-2 border-brand' : 'text-white/80 hover:text-white'}`}
                >
                  {it.label}
                  {typeof it.count === 'number' && (
                    <span className="ml-1 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px]">{it.count}</span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Inline stats under hero */}
        {(data?.stats) && (
          <div className="mb-4 grid grid-cols-4 gap-3 text-center">
            {followersHref ? (
              <Link to={followersHref} className="rounded-xl p-2 transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
                <div className="text-lg font-extrabold">{followersCount}</div>
                <div className="text-xs text-white/70">Followers</div>
              </Link>
            ) : (
              <div>
                <div className="text-lg font-extrabold">{followersCount}</div>
                <div className="text-xs text-white/70">Followers</div>
              </div>
            )}
            {followingHref ? (
              <Link to={followingHref} className="rounded-xl p-2 transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
                <div className="text-lg font-extrabold">{followingCount}</div>
                <div className="text-xs text-white/70">Following</div>
              </Link>
            ) : (
              <div>
                <div className="text-lg font-extrabold">{followingCount}</div>
                <div className="text-xs text-white/70">Following</div>
              </div>
            )}
            {giftsHref ? (
              <Link to={giftsHref} className="rounded-xl p-2 transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
                <div className="text-lg font-extrabold">{new Intl.NumberFormat().format(giftsCount)}</div>
                <div className="text-xs text-white/70 inline-flex items-center">
                  <i className="fa-solid fa-gift text-white/70" aria-hidden="true" />
                </div>
              </Link>
            ) : (
              <div>
                <div className="text-lg font-extrabold">{new Intl.NumberFormat().format(giftsCount)}</div>
                <div className="text-xs text-white/70 inline-flex items-center">
                  <i className="fa-solid fa-gift text-white/70" aria-hidden="true" />
                </div>
              </div>
            )}
            {reactionsHref ? (
              <Link to={reactionsHref} className="rounded-xl p-2 transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
                <div className="text-lg font-extrabold">{data?.stats?.reactions ?? 0}</div>
                <div className="text-xs text-white/70">Reactions</div>
              </Link>
            ) : (
              <div>
                <div className="text-lg font-extrabold">{data?.stats?.reactions ?? 0}</div>
                <div className="text-xs text-white/70">Reactions</div>
              </div>
            )}
          </div>
        )}
        {(aboutHtml || aboutPlain) && (
          <section id="about" data-section className="mb-6">
            <h3 className="text-lg font-extrabold mb-1">{storyTitle || 'About'}</h3>
            {storySubtitle ? <p className="text-white/75 text-sm mb-2">{storySubtitle}</p> : null}
            {aboutHtml ? (
              <MagazineHtml surface="peopleAbout" html={aboutHtml} />
            ) : (
              <p className="text-white/85 whitespace-pre-wrap">{aboutPlain}</p>
            )}
          </section>
        )}

        {data?.links && data.links.length > 0 && (
          <section id="links" data-section className="mb-6">
            <h3 className="text-lg font-extrabold mb-2">Links</h3>
            <div className="flex flex-wrap gap-2">
              {data.links.map((l, i) => (
                <a key={i} href={l.url} className="px-2.5 py-1 text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10" target="_blank" rel="noreferrer">
                  {l.label || l.url}
                </a>
              ))}
            </div>
          </section>
        )}

        {(data?.education?.length || data?.employment?.length) ? (
          <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.education?.length ? (
              <div>
                <h3 className="text-lg font-extrabold mb-2">Education</h3>
                <div>
                  {data.education.map((e: any, i: number) => {
                    const isLast = i === (data.education?.length || 0) - 1
                    const degree = e.degree || e.title || 'Education'
                    const org = e.org || e.school || e.institution || ''
                    const location = e.location || e.city || ''
                    const field = e.field || e.major || ''
                    const gpa = e.gpa || null
                    const logo = e.logoUrl || e.logo || null
                    const fmt = (s?: string | null) => {
                      if (!s) return 'Present'
                      try {
                        const parts = String(s).split('-')
                        const y = Number(parts[0])
                        const m = Number(parts[1] || 1)
                        const MMM = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Math.max(0, m - 1)]
                        return `${MMM} ${isFinite(y) ? y : ''}`.trim()
                      } catch { return String(s) }
                    }
                    const start = e.start || e.start_date || e.from
                    const end = (e.end === null ? null : (e.end || e.end_date || e.to))
                    const range = start || end ? `${fmt(start)} – ${fmt(end ?? null)}` : ''
                    const summary = e.summary || e.notes || ''
                    return (
                      <div key={i} className="flex">
                        {/* timeline rail */}
                        <div className="w-4 flex flex-col items-center">
                          <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                          {!isLast && <div className="w-px flex-1 bg-white/15 my-1" />}
                        </div>
                        <div className="flex-1 ml-2 p-3 rounded-xl border border-white/10 bg-white/5 mb-3">
                          <div className="flex items-start gap-3">
                            {logo ? (
                              <img src={logo} alt={org} className="h-8 w-8 rounded-md object-cover" />
                            ) : (
                              <div className="h-8 w-8 rounded-md bg-white/10" />
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-white">{degree}</div>
                              <div className="text-white/90 text-sm">{org}{location ? ` • ${location}` : ''}</div>
                              {range && <div className="text-xs text-white/70 mt-1">{range}</div>}
                              <div className="mt-2 flex flex-wrap gap-2">
                                {field && <span className="px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-[11px]">{field}</span>}
                                {gpa && <span className="px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-[11px]">GPA {gpa}</span>}
                              </div>
                              {summary && <div className="mt-2 text-sm text-white/85 whitespace-pre-wrap">{summary}</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}
            {data.employment?.length ? (
              <div>
                <h3 className="text-lg font-extrabold mb-2">Experience</h3>
                <ul className="space-y-2 text-sm text-white/85">
                  {data.employment.map((e: any, i: number) => (
                    <li key={i} className="p-2 rounded-lg bg-white/5">
                      <div className="font-semibold">{e.title}</div>
                      <div>{e.company}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ) : null}

        {data?.skills?.length ? (
          <section className="mb-6">
            <h3 className="text-lg font-extrabold mb-2">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {data.skills.map((s: any, i: number) => (
                <span key={i} className="px-2.5 py-1 text-xs rounded-full border border-white/10 bg-white/5">{s.name}</span>
              ))}
            </div>
          </section>
        ) : null}

        {/* Subscription CTA banner (only when enabled) */}
        {showSubCta && (
          <section className="mb-6" aria-label="Subscribe CTA">
            <SectionHeader
              title="Subscriptions"
              subtitle="Subscriber-only drops and premium Pixes."
            />
            <SubscriptionCtaBanner
              creatorName={hero?.displayName || 'this creator'}
              creatorHandle={(hero?.handle || '').replace(/^@/, '')}
              creator={creatorSubsPublic}
              subscribed={subscribed}
              canSubscribe={!isViewingOwnProfile}
              onMessage={() => {
                if (!creatorUserId) return
                openContactIntentModal(
                  creatorUserId,
                  hero?.displayName || 'this user',
                  { postId: publicId || undefined },
                  hero?.handle || undefined
                )
              }}
              onSubscribe={() => {
                if (!getToken()) { navigate('/login'); return }
                setSubError(null)
                setSubOpen(true)
              }}
            />
          </section>
        )}

        {/* Photos (albums + visibility) */}
        {(() => {
          const albums = Array.isArray((data as any)?.photo_albums) ? (((data as any).photo_albums as any[]) || []) : []
          if (albums.length === 0) return null

          const openSubscribe = () => {
            if (!getToken()) { navigate('/login'); return }
            setSubError(null)
            setSubOpen(true)
          }

          const unlockPpvAlbum = async (albumId: string, price: number) => {
            if (!getToken()) { navigate('/login'); return }
            const ok = confirm(`Unlock this album for ${price} PIXE? This unlock is permanent.`)
            if (!ok) return
            try {
              await unlockPixePhotoAlbum(albumId, newIdempotencyKey())
              setToast({ visible: true, kind: 'success', celebrate: false, emoji: 'OK', label: 'Unlocked', detail: 'Album unlocked.' })
              reload()
            } catch (e: any) {
              const msg = e?.response?.data?.detail || e?.message || 'Unlock failed'
              setToast({ visible: true, kind: 'error', celebrate: false, emoji: '!', label: 'Unlock failed', detail: msg })
            }
          }

          const sortedAlbums = albums.slice().sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0))
          return (
            <section id="photos" data-section className="mb-8" aria-label="Photos">
              <div className="mb-3 flex items-center gap-2">
                <h3 className="text-lg font-extrabold">Photos</h3>
                <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">
                  {sortedAlbums.reduce((sum, a) => sum + Number(a?.photo_count ?? 0), 0)}
                </div>
              </div>

              <div className="space-y-6">
                {sortedAlbums.map((al) => {
                  const albumId = String(al?.id ?? '')
                  const title = String(al?.title || 'Album')
                  const visibility = String(al?.visibility || 'public')
                  const isLocked = Boolean(al?.is_locked)
                  const unlockPrice = Number(al?.unlock_price_pixe ?? 0) || 0
                  const count = Number(al?.photo_count ?? 0)
                  const preview = Array.isArray(al?.preview_photos) ? (al.preview_photos as any[]) : []
                  const visibilityLabel =
                    visibility === 'subscribers' ? 'Subscribers' : visibility === 'ppv' ? `PPV${unlockPrice ? ` ${unlockPrice}` : ''}` : 'Public'
                  const pillCls =
                    visibility === 'public'
                      ? 'border-white/15 bg-white/5 text-white/75'
                      : visibility === 'subscribers'
                        ? 'border-sky-400/25 bg-sky-500/15 text-sky-100'
                        : 'border-amber-300/25 bg-amber-400/10 text-amber-100'

                  const albumPhotos = unlockedPhotoItems.filter((p: any) => {
                    const pid = (p?.album_id ?? null) as any
                    if (pid === albumId) return true
                    // Backward-compat: null album photos belong to the default "Photos" album.
                    if (pid == null && title.toLowerCase() === 'photos') return true
                    return false
                  })

                  return (
                    <div key={albumId} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-semibold truncate">{title}</div>
                            <div className="px-2 py-0.5 rounded-full bg-black/20 border border-white/10 text-[11px] text-white/75">
                              {count}
                            </div>
                            <div className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${pillCls}`}>
                              {visibilityLabel}
                            </div>
                          </div>
                        </div>
                        {isLocked ? (
                          <div className="shrink-0 flex items-center gap-2">
                            {visibility === 'subscribers' ? (
                              <button
                                type="button"
                                onClick={openSubscribe}
                                disabled={!subsEnabled}
                                className="px-3 py-1.5 rounded-lg border border-sky-400/30 bg-sky-500/20 hover:bg-sky-500/30 text-xs font-semibold disabled:opacity-50"
                              >
                                Subscribe to unlock
                              </button>
                            ) : visibility === 'ppv' ? (
                              <button
                                type="button"
                                onClick={() => void unlockPpvAlbum(albumId, unlockPrice || 0)}
                                disabled={!unlockPrice}
                                className="px-3 py-1.5 rounded-lg border border-amber-300/25 bg-amber-400/10 hover:bg-amber-400/15 text-xs font-semibold disabled:opacity-50"
                              >
                                Unlock{unlockPrice ? ` for ${unlockPrice} PIXE` : ''}
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled
                                className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-xs font-semibold opacity-60 cursor-not-allowed"
                              >
                                Locked
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>

                      {isLocked ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {preview.slice(0, 8).map((ph: any, i: number) => {
                            const src = String(ph?.thumb_url_512 || '')
                            const caption = (ph?.caption || '').toString()
                            return (
                              <button
                                type="button"
                                key={ph?.id ?? i}
                                onClick={() => {
                                  if (visibility === 'subscribers') openSubscribe()
                                  else if (visibility === 'ppv') void unlockPpvAlbum(albumId, unlockPrice || 0)
                                  else setToast({ visible: true, kind: 'error', celebrate: false, emoji: 'LOCK', label: 'Locked album', detail: 'This album is locked.' })
                                }}
                                className="group relative block aspect-square rounded-xl overflow-hidden border border-white/10 bg-black/30 text-left"
                                title={visibility === 'subscribers' ? 'Subscribe to unlock' : visibility === 'ppv' ? 'Unlock album' : 'Locked'}
                              >
                                {src ? (
                                  <>
                                    <img src={src} alt="Locked preview" className="h-full w-full object-cover blur-[2px] opacity-90" loading="lazy" />
                                    <div className="absolute inset-0 bg-black/35" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="px-2 py-0.5 rounded-full border border-white/15 bg-black/50 text-[11px] text-white/90">
                                        Locked
                                      </div>
                                    </div>
                                    {caption ? (
                                      <div className="absolute left-2 right-2 bottom-2 text-[11px] text-white/85 line-clamp-1">
                                        {caption}
                                      </div>
                                    ) : null}
                                  </>
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center text-xs text-white/60">
                                    Locked
                                  </div>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      ) : albumPhotos.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {albumPhotos.map((ph: any) => {
                            const id = String(ph?.id ?? '')
                            const src = String(ph?.thumb_url_512 || ph?.image_url || '')
                            const original = String(ph?.image_url || '')
                            const caption = (ph?.caption || '').toString()
                            const isFeatured = Boolean(ph?.is_featured)
                            const span = isFeatured ? 'md:col-span-2 md:row-span-2' : ''
                            const alt = caption ? caption : 'Profile photo'
                            return (
                              <button
                                type="button"
                                key={id}
                                onClick={() => {
                                  const idx = lightboxPhotos.findIndex((p) => p.id === id)
                                  if (idx < 0) return
                                  setPhotoLightboxIndex(idx)
                                  setPhotoLightboxOpen(true)
                                  if (id) {
                                    try { history.replaceState(null, '', `#photo=${encodeURIComponent(id)}`) } catch {}
                                  }
                                }}
                                className={`group relative block aspect-square ${span} rounded-xl overflow-hidden border border-white/10 bg-white/5 text-left`}
                                title={caption ? caption : 'View photo'}
                              >
                                {src || original ? (
                                  <>
                                    <img
                                      src={src || original}
                                      alt={alt}
                                      className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                                      loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <div className="absolute left-2 right-2 bottom-2 flex items-end justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <div className="min-w-0">
                                        {isFeatured ? (
                                          <div className="inline-flex items-center px-2 py-0.5 rounded-full border border-amber-300/30 bg-amber-400/15 text-amber-100 text-[11px] font-semibold">
                                            Featured
                                          </div>
                                        ) : null}
                                        {caption ? (
                                          <div className="mt-1 text-[11px] text-white/80 line-clamp-1">{caption}</div>
                                        ) : null}
                                      </div>
                                      <div className="shrink-0 px-2 py-0.5 rounded-full border border-white/15 bg-black/40 text-[11px] text-white/85">
                                        View
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center text-xs text-white/60">Photo</div>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-xs text-white/60">No photos in this album yet.</div>
                      )}
                    </div>
                  )
                })}
              </div>

              <Lightbox
                isOpen={photoLightboxOpen}
                photos={lightboxPhotos}
                startIndex={photoLightboxIndex}
                onClose={() => {
                  setPhotoLightboxOpen(false)
                  try {
                    if (location.hash.startsWith('#photo=')) history.replaceState(null, '', location.pathname + location.search)
                  } catch {}
                }}
              />
            </section>
          )
        })()}

        {pixePros?.is_enabled ? (
          <PixeProsSection
            pros={pixePros}
            isSelf={isViewingOwnProfile}
            profileUserId={creatorUserId}
            displayName={hero?.displayName || null}
            profileHandle={profileHandle || null}
            onMessage={() => {
              if (!creatorUserId) return
              openContactIntentModal(
                creatorUserId,
                hero?.displayName || 'this user',
                { postId: publicId || undefined },
                hero?.handle || undefined
              )
            }}
          />
        ) : null}

        {pixePros?.is_enabled && profileHandle ? (
          <section id="booking" data-section className="mb-8" aria-label="Booking">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-lg font-extrabold">Booking</h3>
              <div className="text-xs text-white/70">Book this pro</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="mb-3 flex flex-wrap items-end gap-3">
                {bookingServices.length > 0 ? (
                  <label className="text-sm">
                    <div className="mb-1 text-xs text-white/60">Service</div>
                    <select
                      value={bookingServiceId ?? ''}
                      onChange={(e) => setBookingServiceId(e.target.value ? Number(e.target.value) : null)}
                      className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    >
                      {bookingServices.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name} ({service.duration_minutes}m{service.capacity > 1 ? ` • cap ${service.capacity}` : ''}{service.price_cents != null ? ` • $${(service.price_cents / 100).toFixed(2)}` : ''})
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {bookingStaffList.length > 0 ? (
                  <label className="text-sm">
                    <div className="mb-1 text-xs text-white/60">Staff</div>
                    <select
                      value={bookingStaffMode === 'SPECIFIC' ? (bookingStaffId ?? '') : ''}
                      onChange={(e) => {
                        const next = e.target.value ? Number(e.target.value) : null
                        setBookingStaffMode(next ? 'SPECIFIC' : 'ANY')
                        setBookingStaffId(next)
                      }}
                      className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    >
                      <option value="">Any staff</option>
                      {bookingStaffList.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.user.display_name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <div className="text-sm">
                  <div className="mb-1 text-xs text-white/60">Date</div>
                  <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/90">
                    {bookingDate || 'Select a day'}
                  </div>
                </div>
                <div className="text-xs text-white/65">Duration: {bookingDurationMinutes} min</div>
                <NextAvailableButton loading={bookingFindingNext} onFind={onFindNextAvailableBookingDay} />
              </div>
              <div className="mb-3">
                <BookingCalendar
                  selectedDate={bookingDate}
                  visibleMonth={bookingVisibleMonth || monthStartIso(bookingDate || todayIsoDate())}
                  onVisibleMonthChange={setBookingVisibleMonth}
                  onSelectDate={setBookingDate}
                  daySummaries={bookingDaySummaries}
                />
              </div>
              {bookingError ? <div className="mb-2 text-sm text-red-300">{bookingError}</div> : null}
              {bookingLoading ? <div className="text-sm text-white/70">Loading slots...</div> : null}
              {!bookingLoading && (bookingReasonCode || !bookingEnabled || bookingClosed) ? (
                <div className="mb-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                  {bookingEmptyMessage}
                </div>
              ) : null}
              {!bookingLoading && bookingSlots.length === 0 && !(bookingReasonCode || !bookingEnabled || bookingClosed) ? (
                <div className="text-sm text-white/70">{bookingEmptyMessage}</div>
              ) : null}
              {!bookingLoading && bookingSlots.length > 0 ? (
                <BookingDaySlots
                  slots={bookingSlots}
                  onPick={(slot) => {
                    setBookingSelected(slot)
                    setBookingOpen(true)
                    setBookingError(null)
                  }}
                />
              ) : null}
              {latestBooking ? (
                <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
                  <div className="text-sm font-semibold">Latest booking</div>
                  <div className="mt-1 text-xs text-white/70">
                    {new Date(latestBooking.starts_at).toLocaleString()} - {new Date(latestBooking.ends_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  <div className="mt-1 text-xs text-white/70">Status: {latestBooking.payment_status}</div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setBookingRescheduleOpen((v) => !v)
                        setBookingRescheduleDateTime(toDateTimeLocalValue(latestBooking.starts_at))
                      }}
                      disabled={bookingActionBusy}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${bookingActionBusy ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'}`}
                    >
                      Reschedule
                    </button>
                    <button
                      type="button"
                      onClick={() => void onCancelLatestBooking()}
                      disabled={bookingActionBusy}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${bookingActionBusy ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                    >
                      Cancel
                    </button>
                  </div>
                  {bookingRescheduleOpen ? (
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <label className="text-xs text-white/70">
                        New start
                        <input
                          type="datetime-local"
                          value={bookingRescheduleDateTime}
                          onChange={(e) => setBookingRescheduleDateTime(e.target.value)}
                          className="mt-1 block rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => void onSubmitRescheduleLatestBooking()}
                        disabled={bookingActionBusy || !bookingRescheduleDateTime}
                        className={`rounded-lg px-3 py-2 text-xs font-semibold ${bookingActionBusy || !bookingRescheduleDateTime ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                      >
                        {bookingActionBusy ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* Posts authored by this user (2 rows) */}
        {(postsLoading || posts.length > 0) && (
          <section id="posts" data-section className="mb-8" aria-label="Posts">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-lg font-extrabold">Posts</h3>
              {!postsLoading && <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">{posts.length}</div>}
            </div>

            {postsLoading && posts.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-[8/9] rounded-xl bg-white/5 border border-white/10" />
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {visiblePosts.map((p) => (
                    <PixeCard
                      key={p.id}
                      title={p.title}
                      subtitle={p.subtitle || p.user?.displayName}
                      image={resolveLandscapeCover(p)}
                      href={p.format === 'job' && (p as any)?.job?.id ? `/jobs/${(p as any).job.id}` : `/p/${p.id}`}
                      meta={p.user?.handle}
                      badge={
                        paidBadgeLabel(
                          (p?.access_level || 'FREE') as AccessLevel,
                          (p as any)?.required_tier_rank ?? null
                        )
                      }
                    />
                  ))}
                </div>

                {canSeeMore && (
                  <div className="pt-4 flex justify-center">
                    <button onClick={onSeeMore} className="px-4 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm">
                      {postsLoading ? 'Loading…' : 'See more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Products the user is selling (2 rows) */}
        {(productsLoading || products.length > 0) && (
          <section id="products" data-section className="mb-8" aria-label="Products">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-lg font-extrabold">Products</h3>
              {!productsLoading && <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">{products.length}</div>}
            </div>

            {productsLoading && products.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-[8/9] rounded-xl bg-white/5 border border-white/10" />
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {visibleProducts.map((l) => {
                    const image = l.product?.thumbnail_media?.public_url || l.images?.[0]?.public_url || null
                    const title = l.product?.title || 'Product'
                    const subtitle = l.product?.subtitle || l.seller?.handle || l.seller?.name || ''
                    const href = `/store/i/${l.public_id}`
                    return (
                      <PixeCard key={l.public_id} title={title} subtitle={subtitle} image={image || undefined} href={href} price={l.price_pixe || null} />
                    )
                  })}
                </div>

                {canSeeMoreProducts && (
                  <div className="pt-4 flex justify-center">
                    <button onClick={onSeeMoreProducts} className="px-4 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm">
                      {productsLoading ? 'Loading…' : 'See more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Comments on products/services — show section even if empty */}
        <section id="comments" data-section className="mb-10" aria-label="Product comments">
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-lg font-extrabold">Comments on their products</h3>
            {!productCommentsLoading && (
              <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">{productComments.length}</div>
            )}
          </div>

          {productCommentsLoading && productComments.length === 0 ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 rounded-lg bg-white/5 border border-white/10" />
              ))}
            </div>
          ) : productComments.length > 0 ? (
            <div className="divide-y divide-white/10 border-y border-white/10">
              {productComments.map(({ listingId, comment }, i) => {
                const listing = products.find((l) => l.public_id === listingId)
                const image = listing?.product?.thumbnail_media?.public_url || listing?.images?.[0]?.public_url || null
                const title = listing?.product?.title || 'Product'
                const href = listing ? `/store/i/${listing.public_id}` : undefined
                const time = comment.created_at ? new Date(comment.created_at).toLocaleDateString() : ''
                const likeCount = (comment.reactions?.like ?? 0) as number
                return (
                  <article key={`${String(listingId)}-${String(comment.id)}-${i}`} className="py-4">
                    <header className="mb-2 flex items-center gap-3 text-sm">
                      {image ? <img src={image} className="h-10 w-10 rounded-md object-cover" /> : <div className="h-10 w-10 rounded-md bg-white/10" />}
                      <div className="min-w-0">
                        <div className="font-semibold line-clamp-1">
                          {href ? <a href={href} className="hover:underline">{title}</a> : title}
                        </div>
                        <div className="text-white/70 text-xs line-clamp-1">{comment.author?.name || comment.author?.handle || 'Anonymous'} • {time}</div>
                      </div>
                      <div className="ml-auto text-white/70 text-xs">❤ {likeCount}</div>
                    </header>
                    <div className="text-white/90 whitespace-pre-wrap line-clamp-4">{comment.body_plain || comment.body || ''}</div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-white/70">No comments yet</div>
          )}

          {productCommentsCanMore && (
            <div className="pt-4 flex justify-center">
              <button onClick={loadMoreProductComments} className="px-4 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm">See more comments</button>
            </div>
          )}
        </section>

        {/* Featured Works */}
        {worksFeatured.length > 0 && (
          <section className="mb-8">
            <h3 className="text-lg font-extrabold mb-3">Featured Works</h3>
            <div className="space-y-3">
              {worksFeatured.map((w: any, i: number) => {
                const cover = w.cover || w.image || w.coverImage || ''
                const title = w.title || w.name || 'Untitled'
                const snippet = w.snippet || w.excerpt || ''
                const meta = w.meta || ''
                const href = w.postId ? `/p/${w.postId}` : undefined
                return (
                  <a key={i} href={href} className={`flex gap-3 rounded-lg p-2 hover:bg-white/5 border border-white/10 ${href ? '' : 'pointer-events-none'}`}>
                    {cover ? (
                      <img src={cover} alt={title} className="h-24 w-32 object-cover rounded-md" />
                    ) : (
                      <div className="h-24 w-32 rounded-md bg-white/5" />
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold text-white line-clamp-2">{title}</div>
                      {meta && <div className="text-xs text-white/70 mt-1">{meta}</div>}
                      {snippet && <div className="text-sm text-white/85 mt-1 line-clamp-2">{snippet}</div>}
                    </div>
                  </a>
                )
              })}
            </div>
          </section>
        )}

        {/* Recent Works */}
        {worksRecent.length > 0 && (
          <section className="mb-8">
            <h3 className="text-lg font-extrabold mb-3">Recent Works</h3>
            <div className="space-y-3">
              {worksRecent.map((w: any, i: number) => {
                const cover = w.cover || w.image || w.coverImage || ''
                const title = w.title || w.name || 'Untitled'
                const snippet = w.snippet || w.excerpt || ''
                const meta = w.meta || ''
                const href = w.postId ? `/p/${w.postId}` : undefined
                return (
                  <a key={i} href={href} className={`flex gap-3 rounded-lg p-2 hover:bg-white/5 border border-white/10 ${href ? '' : 'pointer-events-none'}`}>
                    {cover ? (
                      <img src={cover} alt={title} className="h-24 w-32 object-cover rounded-md" />
                    ) : (
                      <div className="h-24 w-32 rounded-md bg-white/5" />
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold text-white line-clamp-2">{title}</div>
                      {meta && <div className="text-xs text-white/70 mt-1">{meta}</div>}
                      {snippet && <div className="text-sm text-white/85 mt-1 line-clamp-2">{snippet}</div>}
                    </div>
                  </a>
                )
              })}
            </div>
          </section>
        )}

        {/* Reviews on this profile */}
        {publicId && (
          <section id="reviews" data-section>
            <ReviewSummary subjectId={(data as any)?.review_subject_id ?? null} fallbackCount={(data as any)?.reviews?.length ?? 0} />
            <ReviewsInline target={{ type: 'person', id: publicId }} />
          </section>
        )}

        {/* Comments they've left */}
        {commentsLeft.length > 0 && (
          <section className="mt-10" aria-label="Comments they've left">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-lg font-extrabold">Comments they've left</h3>
              <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">{commentsLeft.length}</div>
            </div>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {commentsLeft.map((c: any, i: number) => (
                <div key={c.id ?? i} className="py-4">
                  {c.onPostTitle && (
                    <div className="text-xs text-white/70 mb-1">on <span className="text-white font-semibold">{c.onPostTitle}</span>{c.date ? ` • ${c.date}` : ''}</div>
                  )}
                  <div className="text-white/90 whitespace-pre-wrap">{c.text || c.body || ''}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Profile comments (curated quotes) */}
        {profileComments.length > 0 && (
          <section className="mt-10" aria-label="Profile comments">
            <h3 className="text-lg font-extrabold mb-3">Comments about {hero?.displayName || 'this user'}</h3>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {profileComments.map((c: any, i: number) => (
                <div key={c.id ?? i} className="py-4">
                  <div className="mb-1 flex items-center gap-2 text-sm">
                    {c.avatarUrl ? (
                      <img src={c.avatarUrl} className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-white/10" />
                    )}
                    <div className="font-semibold">{c.author || c.handle || 'Anonymous'}</div>
                    {c.date && <div className="text-white/70 text-xs">{c.date}</div>}
                  </div>
                  <div className="text-white/90 whitespace-pre-wrap">{c.text || c.body || ''}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Overlay actions rail */}
      {publicId && (
        <PeopleActionRail
          publicId={publicId}
          avatarUrl={resolveMediaUrl(hero?.avatar) || hero?.avatar}
          displayName={hero?.displayName}
          handle={hero?.handle}
          counts={{ likes: counts?.likes ?? 0, saves: counts?.saves ?? 0, shares: counts?.shares ?? 0, comments: 0 }}
          shareUrl={data?._meta?.public_id ? `/people/i/${data._meta.public_id}` : undefined}
          onOpenComments={openComments}
          onGift={() => setGiftOpen(true)}
          onReport={openReport}
          onMessage={onMessageFromRail}
          canMessage={Boolean(!isViewingOwnProfile && creatorUserId)}
        />
      )}

      {/* Comments */}
      {publicId && (
        <CommentsDrawer open={commentsOpen} onClose={closeComments} target={{ type: 'person', id: publicId }} />
      )}

      {/* Gift Drawer + Toast */}
      <GiftDrawer
        open={giftOpen}
        onClose={() => setGiftOpen(false)}
        creatorName={hero?.displayName}
        avatarUrl={resolveMediaUrl(hero?.avatar) || hero?.avatar}
        catalog={catalog}
        onSend={({ gift }) => handleSendGift({ gift })}
      />
      <GiftToast visible={toast.visible} emoji={toast.emoji} label={toast.label} detail={toast.detail} kind={toast.kind} onHide={hideToast} />
      <GiftCelebration visible={Boolean(toast.visible && toast.kind === 'success' && toast.celebrate)} />
      {bookingOpen && bookingSelected ? (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#0b0b10] p-5">
            <div className="mb-3 text-lg font-bold">Book appointment</div>
            <div className="mb-3 text-sm text-white/70">
              {new Date(bookingSelected.starts_at).toLocaleString()} - {new Date(bookingSelected.ends_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
            {bookingQuote ? (
              <div className="mb-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/75">
                <div>Duration: {bookingQuote.duration_minutes} min</div>
                <div>Service price: {bookingQuote.price_cents == null ? 'N/A' : `$${(bookingQuote.price_cents / 100).toFixed(2)}`}</div>
                <div>Deposit due now: ${((bookingQuote.deposit_amount_cents || 0) / 100).toFixed(2)}</div>
                <div>Platform fee (10%): ${((bookingQuote.platform_fee_cents || 0) / 100).toFixed(2)}</div>
              </div>
            ) : null}
            {bookingClientSecret ? (
              stripePromise ? (
                <>
                  <div className="mb-3 text-xs text-amber-200">
                    Complete payment within {String(Math.floor(bookingPaymentRemainingSeconds / 60)).padStart(2, '0')}:{String(bookingPaymentRemainingSeconds % 60).padStart(2, '0')}
                  </div>
                  {bookingPaymentRemainingSeconds > 0 ? (
                    <Elements stripe={stripePromise} options={{ clientSecret: bookingClientSecret, appearance: { theme: 'night' as const } }}>
                      <BookingPaymentConfirm
                        busy={bookingPaymentSubmitting}
                        setBusy={setBookingPaymentSubmitting}
                        onError={(message) => setBookingError(message || null)}
                        onSuccess={() => {
                          void onBookingPaymentSuccess()
                        }}
                      />
                    </Elements>
                  ) : (
                    <div className="text-sm text-amber-200">Payment session expired — please pick a time again.</div>
                  )}
                </>
              ) : (
                <div className="text-sm text-red-300">Stripe is not configured in this environment.</div>
              )
            ) : (
              <div className="space-y-2">
                <input
                  value={bookingName}
                  onChange={(e) => setBookingName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <input
                  value={bookingEmail}
                  onChange={(e) => setBookingEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <input
                  value={bookingPhone}
                  onChange={(e) => setBookingPhone(e.target.value)}
                  placeholder="Phone (optional)"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <textarea
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  className="min-h-[80px] w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
            )}
            {bookingError ? <div className="mt-2 text-sm text-red-300">{bookingError}</div> : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setBookingOpen(false)
                  setBookingClientSecret('')
                  setBookingPaymentExpiresAt(null)
                }}
                className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10"
              >
                Cancel
              </button>
              {!bookingClientSecret ? (
                <button
                  type="button"
                  onClick={handleCreateProBooking}
                  disabled={bookingSubmitting}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                >
                  {bookingSubmitting ? 'Booking...' : 'Confirm'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {publicId && (
        <ReportModal
          open={reportOpen}
          onClose={closeReport}
          contentType="profile"
          contentId={publicId}
          title={hero?.displayName ? `Report ${hero.displayName}` : 'Report profile'}
        />
      )}

      <ReputationModal
        open={reputationOpen}
        onClose={() => setReputationOpen(false)}
        reputation={reputation}
      />

      <SubscribeModal
        open={subOpen}
        onClose={() => setSubOpen(false)}
        creatorName={hero?.displayName}
        monthlyPricePixe={creatorSubsPublic?.monthly_price_pixe ?? null}
        tiers={creatorSubsPublic?.tiers ?? null}
        features={(creatorSubsPublic as any)?.features ?? null}
        receivePostsEmail={receivePostsEmail}
        onChangeReceivePostsEmail={setReceivePostsEmail}
        busy={subBusy}
        error={subError}
        onConfirm={async (tierId, opts) => {
          if (!creatorUserId) return
          setSubError(null)
          setSubBusy(true)
          try {
            await subscribeToCreator(creatorUserId, { idempotencyKey: newIdempotencyKey('sub'), tierId: tierId ?? null })
            await patchCreatorEmailPreference({
              creator_id: creatorUserId,
              receive_posts: Boolean(opts?.receivePostsEmail),
            })
            setSubscribed(true)
            setSubOpen(false)
          } catch (e: any) {
            setSubError(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Subscribe failed')
          } finally {
            setSubBusy(false)
          }
        }}
      />
      <ContactIntentModal
        open={contactIntentOpen}
        onClose={() => {
          if (contactIntentBusy) return
          setContactIntentOpen(false)
        }}
        onSelect={onContactIntentSelect}
        targetUserId={contactIntentTargetId}
        targetUserName={contactIntentTargetName}
        context={contactIntentContext}
        busy={contactIntentBusy}
      />
    </div>
  )
}

function SubscriptionCtaBanner({
  creatorName,
  creatorHandle,
  creator,
  subscribed,
  canSubscribe,
  onSubscribe,
  onMessage,
}: {
  creatorName: string
  creatorHandle: string
  creator: CreatorSubscriptionSettingsPublic | null
  subscribed: boolean
  canSubscribe?: boolean
  onSubscribe: () => void
  onMessage?: () => void
}) {
  const tiers = (creator?.tiers || []).filter((t) => (t as any)?.is_active !== false).sort((a, b) => (a.rank || 0) - (b.rank || 0))
  const minTier = tiers.length ? tiers.reduce((m, t) => (t.monthly_price_pixe < m.monthly_price_pixe ? t : m), tiers[0]) : null
  const pitch = (creator?.pitch_text || '').trim() || 'Get premium Pixes and subscriber-only drops.'
  const benefits = Array.isArray(creator?.subscription_benefits) ? creator.subscription_benefits.filter(Boolean) : []
  const recent = Array.isArray(creator?.subscription_recent_posts) ? creator.subscription_recent_posts : []
  const subscriberCount = Number(creator?.subscription_subscriber_count || 0)
  const memberSince = creator?.viewer_subscription_started_at ? new Date(creator.viewer_subscription_started_at) : null
  const memberSinceLabel = memberSince && !Number.isNaN(memberSince.getTime()) ? memberSince.toLocaleDateString() : null
  const priceLine = minTier
    ? `From ${minTier.monthly_price_pixe} PIXE / 30 days`
    : creator?.monthly_price_pixe
      ? `${creator.monthly_price_pixe} PIXE / 30 days`
      : '-'

  return (
    <div className="rounded-2xl border border-white/10 bg-[radial-gradient(900px_400px_at_10%_0%,rgba(56,189,248,0.16),transparent_55%),radial-gradient(900px_400px_at_100%_20%,rgba(16,185,129,0.10),transparent_55%)] p-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="text-lg font-extrabold">Subscribe to {creatorName}</div>
          <div className="mt-1 text-sm text-white/75">{pitch}</div>
          <div className="mt-2 text-xs text-white/70">
            {subscriberCount.toLocaleString()} {subscriberCount === 1 ? 'subscriber' : 'subscribers'}
            {subscribed && memberSinceLabel ? ` - Member since ${memberSinceLabel}` : ''}
          </div>
          <div className="mt-2 text-xs text-white/60">
            {priceLine}
            {minTier ? ' - Higher tiers include lower tiers.' : ''}
            {' - '}30-day periods. Manual renewals for now.
          </div>
          <div className="mt-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-white/65">What you get</div>
            <ul className="mt-2 space-y-1 text-sm text-white/85">
              {(benefits.length ? benefits : ['Subscriber-only posts', 'Early access to drops', 'Premium Pixes']).slice(0, 3).map((item, idx) => (
                <li key={`${item}-${idx}`} className="flex items-start gap-2">
                  <span aria-hidden="true" className="mt-[1px] text-white/60">*</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {subscribed ? (
            <>
              <span className="rounded-full px-3 py-2 text-sm font-semibold border border-emerald-400/30 bg-emerald-500/10 text-emerald-50">
                Subscribed
              </span>
              <Link
                to="/settings/subscriptions"
                className="rounded-full px-4 py-2 text-sm font-semibold border border-white/15 bg-white/5 text-white/90 hover:bg-white/10"
              >
                Manage
              </Link>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={canSubscribe ? onSubscribe : undefined}
                disabled={!canSubscribe}
                title={!canSubscribe ? 'You cannot subscribe to your own account.' : undefined}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  canSubscribe ? 'bg-brand text-black hover:brightness-110' : 'bg-white/10 text-white/60 cursor-not-allowed'
                }`}
              >
                Subscribe
              </button>
              <Link
                to={creatorHandle ? `/inbox/compose?to=@${encodeURIComponent(creatorHandle)}` : '/inbox'}
                onClick={(e) => {
                  if (!onMessage) return
                  e.preventDefault()
                  onMessage()
                }}
                className="rounded-full px-4 py-2 text-sm font-semibold border border-white/15 bg-white/5 text-white/90 hover:bg-white/10"
              >
                Message
              </Link>
            </>
          )}
        </div>
      </div>
      <div className="mt-4 border-t border-white/10 pt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-white/65">Recent subscriber-only posts</div>
        {recent.length ? (
          <div className="mt-2 space-y-2">
            {recent.slice(0, 3).map((post) => (
              <Link
                key={post.id}
                to={post.url || `/p/${post.id}`}
                className="block rounded-xl border border-white/10 bg-black/20 p-3 hover:bg-white/5"
              >
                <div className="flex items-center gap-2 text-xs text-white/65">
                  <span aria-hidden="true">LOCKED</span>
                  <span>{post.published_at ? new Date(post.published_at).toLocaleDateString() : ''}</span>
                </div>
                <div className="mt-1 text-sm font-semibold text-white">{post.title || 'Untitled'}</div>
                <div className="mt-1 text-sm text-white/75 line-clamp-2">{post.snippet || 'Subscriber-only content.'}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="mt-2 text-sm text-white/65">No subscriber-only posts yet.</div>
        )}
      </div>
    </div>
  )
}


